/**
 * Default stream → cache wiring (P2 §3.6): one table from event type to cache patch/invalidation. Features add their own
 * handlers with `useStreamEvent()`; these defaults keep the shared entity caches (core/entities.ts) fresh everywhere.
 * Handlers are idempotent: replays after reconnect only re-invalidate.
 */
import type { QueryClient } from '@tanstack/react-query';
import { connect, connectFixture, close, on } from './stream';
import { qk } from './entities';
import { invalidateSoon } from './refetch';
import type { DataProvider } from './provider';
import type { StreamEvent, TechLive } from './types';

export function startRealtime(data: DataProvider, qc: QueryClient) {
  const offs: (() => void)[] = [];
  const inval = (key: readonly unknown[]) => invalidateSoon(qc, key);
  const jobChanged = (e: StreamEvent) => {
    const id = (e.data.job_id as string) ?? e.entity_id;
    if (id) inval(qk.job(id));
    inval(qk.jobs());
  };
  offs.push(
    on('job.created', jobChanged), on('job.updated', jobChanged), on('job.status_changed', jobChanged),
    on('job.assigned', jobChanged), on('job.unassigned', jobChanged), on('job.line_changed', jobChanged), on('job.note_added', jobChanged),
    on('estimate.sent', jobChanged), on('estimate.approved', jobChanged), on('estimate.declined', jobChanged),
    on('customer.created', () => inval(qk.customers())), on('customer.updated', (e) => { inval(qk.customer(e.entity_id)); inval(qk.customers()); }),
    on('vehicle.created', () => inval(['vehicles'])), on('vehicle.updated', () => inval(['vehicles'])),
    on('technician.created', () => inval(qk.techs)), on('technician.updated', () => inval(qk.techs)),
    on('catalog.imported', () => inval(qk.catalog)), on('catalog.updated', () => inval(qk.catalog)),
    // Ephemeral GPS: patch the live list in place (no refetch; ~1 per tech per 10 s).
    on('tech.location', (e) => {
      qc.setQueryData<TechLive[]>(qk.techsLive, (prev) => prev?.map((t) => (t.tech_id === e.data.tech_id
        ? { ...t, lat: e.data.lat as number, lng: e.data.lng as number, heading: (e.data.heading as number) ?? t.heading, at: (e.data.at as string) ?? t.at, eta_min: (e.data.eta_min as number | undefined) ?? t.eta_min }
        : t)));
    }),
    on('tech.state', () => inval(qk.techsLive)), on('tech.alert', () => inval(qk.techsLive)), on('tech.stationary', () => inval(qk.techsLive)),
    on('tenant.status_changed', () => inval(['me'])), on('tenant.live', () => inval(['me'])),
  );

  if (data.fixtureDb) connectFixture((emit) => data.fixtureDb!.subscribe(emit));
  else connect({ onReset: () => invalidateSoon(qc, []) });

  return () => { offs.forEach((f) => f()); close(); };
}
